import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { workspaceApi, type Analytics, type Dashboard } from '../../api/workspaces';

export function WorkspaceDashboardPage() {
  const { id = '' } = useParams();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void workspaceApi
      .getDashboard(id)
      .then(setDashboard)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="p-6 text-slate-600">Loading dashboard…</p>;
  if (error || !dashboard) return <p className="p-6 text-red-600">{error || 'Dashboard unavailable'}</p>;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to={`/workspaces/${id}`} className="text-sm text-slate-600 hover:underline">
        ← Workspace
      </Link>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Workspace Dashboard</h1>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(dashboard.stats ?? {}).slice(0, 4).map(([key, value]) => (
          <div key={key} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-sm text-slate-500">{key.replace(/_/g, ' ')}</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{String(value)}</p>
          </div>
        ))}
      </div>

      <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-medium text-slate-900">Widgets</h2>
        {dashboard.widgets?.length ? (
          <ul className="mt-4 grid gap-3 md:grid-cols-2">
            {dashboard.widgets.map((w) => (
              <li key={w.id} className="rounded-lg border border-slate-100 p-4 text-sm">
                <p className="font-medium">{w.title}</p>
                <p className="text-slate-500">{w.type}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-slate-600">No widgets configured yet.</p>
        )}
      </section>
      <Link to={`/workspaces/${id}/analytics`} className="mt-4 inline-block text-sm text-slate-700 hover:underline">
        View analytics
      </Link>
    </div>
  );
}
